import { PickersShortcutsItem } from "@mui/x-date-pickers-pro";
import { DateRange } from "@mui/x-date-pickers-pro/models";
import { DateTime } from "luxon";

export type DateShortcutType = "HISTORICAL" | "FUTURE" | "OTHER";

export type DateRangeShortcut = PickersShortcutsItem<DateRange<DateTime>>;

const RESET: DateRangeShortcut = { label: "Reset", getValue: () => [null, null] };

export const HISTORICAL_SHORTCUTS: DateRangeShortcut[] = [
  {
    // End at this month, not Dec 31, so a mid-year click doesn't imply full-year data.
    label: `${DateTime.now().year}`,
    getValue: () => [DateTime.now().startOf("year"), DateTime.now().endOf("month")],
  },
  {
    label: `${DateTime.now().year - 1}`,
    getValue: () => [
      DateTime.now().minus({ years: 1 }).startOf("year"),
      DateTime.now().minus({ years: 1 }).endOf("year"),
    ],
  },
  {
    label: `${DateTime.now().year - 2}`,
    getValue: () => [
      DateTime.now().minus({ years: 2 }).startOf("year"),
      DateTime.now().minus({ years: 2 }).endOf("year"),
    ],
  },
  {
    label: "This Quarter",
    getValue: () => [DateTime.now().startOf("quarter"), DateTime.now().endOf("quarter")],
  },
  {
    label: "Last Quarter",
    getValue: () => [
      DateTime.now().minus({ quarters: 1 }).startOf("quarter"),
      DateTime.now().minus({ quarters: 1 }).endOf("quarter"),
    ],
  },
  {
    // Trailing 12 months ending yesterday (today excluded — it's incomplete).
    label: "Last 12 Months",
    getValue: () => [
      DateTime.now().minus({ months: 12, days: 1 }),
      DateTime.now().minus({ days: 1 }),
    ],
  },
  RESET,
];

export const FUTURE_SHORTCUTS: DateRangeShortcut[] = [
  {
    label: "This Quarter",
    getValue: () => [DateTime.now().startOf("quarter"), DateTime.now().endOf("quarter")],
  },
  {
    label: "Next Quarter",
    getValue: () => [
      DateTime.now().plus({ quarters: 1 }).startOf("quarter"),
      DateTime.now().plus({ quarters: 1 }).endOf("quarter"),
    ],
  },
  {
    label: "Next 90 Days",
    getValue: () => [DateTime.now(), DateTime.now().plus({ days: 90 })],
  },
  {
    label: "Next 6 Months",
    getValue: () => [DateTime.now(), DateTime.now().plus({ months: 6 })],
  },
  {
    label: "This Year",
    getValue: () => [DateTime.now().startOf("year"), DateTime.now().endOf("year")],
  },
  {
    label: "Last Year",
    getValue: () => [
      DateTime.now().minus({ years: 1 }).startOf("year"),
      DateTime.now().minus({ years: 1 }).endOf("year"),
    ],
  },
  RESET,
];

/** `OTHER` falls back to `HISTORICAL` when no custom list is given. */
export function getDateRangeShortcuts(
  type: DateShortcutType,
  customShortcuts?: DateRangeShortcut[]
): DateRangeShortcut[] {
  switch (type) {
    case "FUTURE":
      return FUTURE_SHORTCUTS;
    case "OTHER":
      return customShortcuts && customShortcuts.length > 0 ? customShortcuts : HISTORICAL_SHORTCUTS;
    case "HISTORICAL":
    default:
      return HISTORICAL_SHORTCUTS;
  }
}
